/**
 * Bridge between the meta router (created in main.ts) and the in-run HUD: the HUD's menu icons
 * and hotkeys open the meta screens as an overlay over the run, read the profile for badges and
 * currencies, and close the overlay when the key ends. `registerMetaScreens` binds it once.
 */
import { type MenuRoute, type MetaHotkeyTarget, menuHotkeys } from '../hud/panels/hotkeys';
import type { MetaRouter } from './context';
import type { ProfileStore } from './profileStore';
import type { RouteId } from './router';
import { installHubHotkeys } from './hubHotkeys';
import { installSfx } from './audio/sfx';

let router: MetaRouter | null = null;
let overlayNote: HTMLElement | null = null;
const KEY_NOTE = 'Key in progress: gear and talent changes apply to your next run.';
/** +field: the world sets its own note (changes apply when the window closes). */
let noteText = KEY_NOTE;

/** Mark an overlay screen (translucent backdrop + "applies next run" note). */
function markOverlay(): void {
  const r = router;
  const s = r?.screen;
  if (!r || !s || !r.isOverlay) return;
  s.el.classList.add('kui-as-overlay', 'km-as-overlay');
  if (typeof document === 'undefined') return;
  if (!overlayNote) {
    overlayNote = document.createElement('div');
    overlayNote.className = 'kui-overlay-note';
  }
  overlayNote.textContent = noteText;
  overlayNote.hidden = !noteText;
  overlayNote.className = s.el.querySelector('.km-header') ? 'km-overlay-note' : 'kui-overlay-note';
  const top = s.el.querySelector('.km-header-center') ?? s.el.querySelector('.kui-topbar');
  if (top && overlayNote.parentElement !== top) top.appendChild(overlayNote);
}

/** Leave the screen only if it agrees (unsaved talents ask first). */
function canLeave(): boolean {
  return !router?.screen?.onBack?.();
}

const target: MetaHotkeyTarget = {
  overlayOpen: () => !!router?.isOverlay,
  screenOpen: () => !!router?.screen,
  toggle(route: MenuRoute, inRun: boolean) {
    const r = router;
    if (!r || !r.has(route)) return;
    const isCur = r.isCurrent(route);
    if (inRun) {
      if (r.isOverlay && isCur) {
        if (canLeave()) r.close();
      } else if (r.isOverlay) {
        if (canLeave()) r.replace(route, { classId: r.context.store.profile.mainClassId });
      } else r.openOverlay(route, { classId: r.context.store.profile.mainClassId });
      return;
    }
    if (isCur) r.back();
    else r.open(route, { classId: r.context.store.profile.mainClassId });
  },
  back() {
    router?.back();
  },
};

export function bindMetaBridge(r: MetaRouter): void {
  router = r;
  r.subscribe(markOverlay);
  menuHotkeys.meta = target;
  menuHotkeys.install();
  // Lobby-only Forge keys (F U T) and the UI sound cues.
  installHubHotkeys({
    state: (typing) => ({
      inRun: !!menuHotkeys.hud?.active(),
      screenOpen: !!router?.screen && !router.isOverlay,
      typing,
      modalOpen: typeof document !== 'undefined' && !!document.querySelector('.kui-modal-backdrop, .km-modal-back'),
    }),
    toggle(route) {
      const rr = router;
      if (!rr || !rr.has(route)) return;
      // Same rule as the menu keys outside a run: the open screen's key goes back.
      if (rr.isCurrent(route)) rr.back();
      else rr.open(route, { classId: rr.context.store.profile.mainClassId });
    },
  });
  installSfx();
}

export const metaBridge = {
  /** The profile store (null before the app bound the router, e.g. in tests or the kit gallery). */
  get store(): ProfileStore | null {
    return router?.context.store ?? null;
  },
  /** Route on top of an open overlay (null when none). Allocation-free (per-frame safe). */
  overlayRoute(): RouteId | null {
    return router?.isOverlay ? (router.current()?.id ?? null) : null;
  },
  /** +field: the overlay note's text (null = the key default). */
  setOverlayNote(text: string | null): void {
    noteText = text ?? KEY_NOTE;
    if (overlayNote) {
      overlayNote.textContent = noteText;
      overlayNote.hidden = !noteText;
    }
  },
  /** Close an in-run overlay (the key ended). No-op outside overlay mode. */
  closeOverlay(): void {
    if (router?.isOverlay) router.close();
  },
};
